import { z } from 'zod';
import { callQueueStrategySchema, callQueueTimeoutActionSchema } from './voip-queue-ring-group';

/**
 * DTO di lettura delle code (SPA tenant): lista code, dashboard realtime e
 * dashboard SLA. Source of truth: `packages/backend/src/routes/telephony/queue-routes.ts`
 * (+ `services/telephony/queue-dashboard*`).
 */

export const queueAgentStatusSchema = z.enum(['Available', 'On Break', 'Logged Out']);
export const queueAgentStateSchema = z.enum(['Waiting', 'Receiving', 'In a queue call']);

export const callQueueAgentListItemSchema = z.object({
  id: z.string(),
  extension_id: z.string(),
  agent_name: z.string(),
  agent_type: z.enum(['callback', 'uuid-standby', 'uuid-bridge']),
  contact: z.string(),
  status: queueAgentStatusSchema,
  state: queueAgentStateSchema,
  tier_level: z.number(),
  tier_position: z.number(),
  calls_answered: z.number(),
  talk_time: z.number(),
  enabled: z.boolean(),
});
export type CallQueueAgentListItem = z.infer<typeof callQueueAgentListItemSchema>;

export const callQueueSettingsSchema = z.object({
  announce_position: z.boolean(),
  announce_position_frequency: z.number(),
  announce_hold_time: z.boolean(),
  answer_mode: z.enum(['answer', 'early_media']),
  media_type: z.enum(['ringback', 'message', 'music', 'silence']),
  media_source: z.enum(['stream_default', 'file']),
  media_file: z.string().optional(),
  announce_voice: z.string().optional(),
});
export type CallQueueSettings = z.infer<typeof callQueueSettingsSchema>;

/** `GET /voip/queues` → `data[]`. */
export const callQueueListItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().optional(),
  extension: z.string(),
  strategy: callQueueStrategySchema,
  max_wait_time: z.number(),
  agents: z.array(callQueueAgentListItemSchema),
  queue_timeout: z.number(),
  queue_timeout_action: callQueueTimeoutActionSchema,
  queue_timeout_destination: z.string().optional(),
  enabled: z.boolean(),
  current_calls: z.number(),
  max_concurrent_calls: z.number(),
  moh_sound: z.string().optional(),
  settings: callQueueSettingsSchema.optional(),
  callback_enabled: z.boolean().optional(),
  callback_max_wait_seconds: z.number().optional(),
  callback_min_position: z.number().optional(),
  callback_max_requests: z.number().optional(),
  tier_rules_apply: z.boolean().optional(),
  tier_rule_wait_second: z.number().optional(),
  tier_rule_wait_multiply_level: z.boolean().optional(),
  tier_rule_no_agent_no_wait: z.boolean().optional(),
  abandoned_resume_allowed: z.boolean().optional(),
  discard_abandoned_after: z.number().optional(),
  max_wait_time_with_no_agent: z.number().optional(),
  max_penalty: z.number().optional(),
  min_penalty: z.number().optional(),
});
export type CallQueueListItem = z.infer<typeof callQueueListItemSchema>;

// ─── Dashboard realtime ─────────────────────────────────────────────────────

export const queueDashboardAgentDetailSchema = z.object({
  id: z.string(),
  extension_id: z.string(),
  agent_name: z.string(),
  status: queueAgentStatusSchema,
  state: queueAgentStateSchema,
  calls_answered: z.number(),
  talk_time: z.number(),
});
export type QueueDashboardAgentDetail = z.infer<typeof queueDashboardAgentDetailSchema>;

export const queueDashboardQueueSchema = z.object({
  id: z.string(),
  name: z.string(),
  extension: z.string(),
  strategy: z.string(),
  enabled: z.boolean(),
  current_calls: z.number(),
  max_concurrent_calls: z.number(),
  waiting_calls: z.number(),
  longest_wait_seconds: z.number(),
  stats_today: z.object({
    total: z.number(),
    answered: z.number(),
    abandoned: z.number(),
    avg_wait_time: z.number(),
    max_wait_time: z.number(),
  }),
  agents: z.object({
    total: z.number(),
    available: z.number(),
    on_break: z.number(),
    logged_out: z.number(),
    on_call: z.number(),
  }),
  agents_detail: z.array(queueDashboardAgentDetailSchema),
});
export type QueueDashboardQueue = z.infer<typeof queueDashboardQueueSchema>;

export const queueDashboardSummarySchema = z.object({
  total_queues: z.number(),
  active_queues: z.number(),
  total_agents: z.number(),
  available_agents: z.number(),
  total_waiting_calls: z.number(),
  total_calls_today: z.number(),
  answered_today: z.number(),
  abandoned_today: z.number(),
});
export type QueueDashboardSummary = z.infer<typeof queueDashboardSummarySchema>;

/** `GET /voip/queues/dashboard/realtime` → `data`. */
export const queueRealtimeDashboardSchema = z.object({
  summary: queueDashboardSummarySchema,
  queues: z.array(queueDashboardQueueSchema),
  timestamp: z.string(),
});
export type QueueRealtimeDashboard = z.infer<typeof queueRealtimeDashboardSchema>;

// ─── Dashboard SLA ──────────────────────────────────────────────────────────

export const queueSlaQueueSchema = z.object({
  queue_id: z.string(),
  queue_name: z.string(),
  extension: z.string(),
  enabled: z.boolean(),
  sla_threshold: z.number(),
  sla_percentage: z.number(),
  answer_rate: z.number(),
  total_calls: z.number(),
  answered_calls: z.number(),
  answered_within_sla: z.number(),
  abandoned_calls: z.number(),
  avg_wait_time: z.number(),
  max_wait_time: z.number(),
  avg_talk_time: z.number(),
  median_wait_time: z.number(),
  p90_wait_time: z.number(),
  hourly_stats: z.array(z.object({
    hour: z.number(),
    total: z.number(),
    answered: z.number(),
    within_sla: z.number(),
    sla_pct: z.number(),
  })),
  agent_performance: z.array(z.object({
    agent_name: z.string(),
    extension_id: z.string(),
    status: z.string(),
    state: z.string(),
    calls_today: z.number(),
    total_calls: z.number(),
    total_talk_time: z.number(),
    avg_handle_time: z.number(),
  })),
});
export type QueueSlaQueue = z.infer<typeof queueSlaQueueSchema>;

export const queueSlaSummarySchema = z.object({
  sla_threshold: z.number(),
  overall_sla: z.number(),
  overall_answer_rate: z.number(),
  total_calls: z.number(),
  answered_calls: z.number(),
  answered_within_sla: z.number(),
  abandoned_calls: z.number(),
  avg_wait_time: z.number(),
});
export type QueueSlaSummary = z.infer<typeof queueSlaSummarySchema>;

/** `GET /voip/queues/dashboard/sla?threshold=N` → `data`. */
export const queueSlaDashboardSchema = z.object({
  summary: queueSlaSummarySchema,
  queues: z.array(queueSlaQueueSchema),
  timestamp: z.string(),
});
export type QueueSlaDashboard = z.infer<typeof queueSlaDashboardSchema>;
